import React, { useCallback, useEffect, useState } from "react";
import { History, Loader2, Undo2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const show = (v) => (v === null || v === undefined || v === "" ? "empty" : String(v));

/** Who changed this record, when, what it said before — and a one-click way to put it back. */
export const RecordHistory = ({ collection, recordId, onReverted }) => {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(
    () =>
      api
        .get(`/records/${collection}/${recordId}/history`)
        .then(({ data }) => setRows(data))
        .catch(() => setRows([])),
    [collection, recordId],
  );

  useEffect(() => {
    if (open && rows === null) load();
  }, [open, rows, load]);

  const revert = async (entry) => {
    if (!window.confirm("Put these values back as they were?")) return;
    setBusy(entry.id);
    try {
      await api.post(`/records/${collection}/${recordId}/history/${entry.id}/revert`);
      toast.success("Change reverted");
      setRows(null);
      await load();
      await onReverted?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2 border-t border-slate-100 pt-3">
      <button
        data-testid="record-history-toggle"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 transition-colors hover:text-slate-900"
      >
        <History size={13} /> Change history
        <span className="ml-1 normal-case tracking-normal text-slate-400">
          — who changed what, with one-click undo
        </span>
      </button>
      {open && (
        <div data-testid="record-history-list" className="mt-3 space-y-3">
          {rows === null && (
            <p className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 size={13} className="animate-spin" /> Loading…
            </p>
          )}
          {rows?.length === 0 && (
            <p className="text-xs text-slate-500">
              No changes recorded yet. From now on, every edit is listed here with who made it.
            </p>
          )}
          {rows?.map((r) => (
            <div key={r.id} className="rounded-md bg-slate-50 px-3 py-2.5 text-xs">
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold text-slate-700">
                  {r.changed_by || r.changed_by_email || "Unknown user"}
                  <span className="ml-2 font-normal text-slate-500">
                    {(r.changed_at || "").slice(0, 16).replace("T", " ")}
                  </span>
                </p>
                <button
                  data-testid={`record-history-revert-${r.id}`}
                  onClick={() => revert(r)}
                  disabled={busy === r.id}
                  title="Put these values back"
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900 disabled:opacity-50"
                >
                  {busy === r.id ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />} Undo
                </button>
              </div>
              <ul className="mt-1.5 space-y-1 text-slate-600">
                {r.changes.map((c, i) => (
                  <li key={`${c.field}-${i}`}>
                    <span className="font-medium">{label(c.field)}</span>
                    {": "}
                    <span className="line-through decoration-slate-400">{show(c.from)}</span>
                    {" → "}
                    <span className="font-medium text-slate-900">{show(c.to)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
